import { useState } from "react";
import { X, CalendarDays, Phone, Heart, MessageCircle, Send, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { AspirasiItem } from "../../Pages/HeroAspirasi/AspirasiList";
import { toggleLike, tambahKomentar, hapusKomentar } from "../../../utils/aspirasiApi";

interface Props {
  open: boolean;
  onClose: () => void;
  data: AspirasiItem | null;
  deviceId: string;
  onPatch: (patch: Partial<AspirasiItem>) => void;
}

const NAMA_KOMENTAR_KEY = "nama_komentar";

const formatTanggal = (date: string) => {
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
};

const formatTanggalSingkat = (date: string) => {
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
};

const getInitial = (nama: string) => {
  return nama
    .split(" ")
    .map((item) => item[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const AspirasiDetailModal = ({ open, onClose, data, deviceId, onPatch }: Props) => {
  const [liking, setLiking] = useState(false);
  const [nama, setNama] = useState(() => {
    try {
      return localStorage.getItem(NAMA_KOMENTAR_KEY) || "";
    } catch {
      return "";
    }
  });
  const [komentarBaru, setKomentarBaru] = useState("");
  const [mengirim, setMengirim] = useState(false);
  const [hapusId, setHapusId] = useState<number | null>(null);

  if (!open || !data) return null;

  const handleLike = async () => {
    if (liking) return;
    setLiking(true);

    const prevLiked = data.liked_by_me;
    const prevTotal = data.total_like;

    onPatch({
      liked_by_me: !prevLiked,
      total_like: prevLiked ? prevTotal - 1 : prevTotal + 1,
    });

    try {
      const res = await toggleLike(data.id, deviceId);
      if (res?.success) {
        onPatch({ liked_by_me: res.liked, total_like: res.total_like });
      } else {
        onPatch({ liked_by_me: prevLiked, total_like: prevTotal });
      }
    } catch {
      onPatch({ liked_by_me: prevLiked, total_like: prevTotal });
    } finally {
      setLiking(false);
    }
  };

  const handleKirimKomentar = async () => {
    if (!nama.trim() || !komentarBaru.trim() || mengirim) return;

    setMengirim(true);

    try {
      const res = await tambahKomentar(data.id, deviceId, nama.trim(), komentarBaru.trim());

      if (res?.success) {
        try {
          localStorage.setItem(NAMA_KOMENTAR_KEY, nama.trim());
        } catch {
          // biarkan, gak fatal kalau gagal simpan nama
        }

        const komentarBaruItem = {
          id: res.id,
          aspirasi_id: data.id,
          device_id: deviceId,
          nama: nama.trim(),
          komentar: komentarBaru.trim(),
          tanggal: new Date().toISOString(),
          is_owner: true,
        };

        onPatch({ komentar: [komentarBaruItem, ...(data.komentar ?? [])] });
        setKomentarBaru("");

        Swal.fire({
          icon: "success",
          title: "Berhasil 🎉",
          text: "Komentar kamu berhasil ditambahkan.",
          confirmButtonColor: "#16a34a",
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: res?.message || "Gagal mengirim komentar, coba lagi.",
          confirmButtonColor: "#dc2626",
        });
      }
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Terjadi kesalahan saat mengirim komentar.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setMengirim(false);
    }
  };

  const handleHapusKomentar = async (id: number) => {
    setHapusId(id);

    try {
      const res = await hapusKomentar(id, deviceId);

      if (res?.success) {
        onPatch({ komentar: (data.komentar ?? []).filter((k) => k.id !== id) });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHapusId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl animate-[fadeIn_.25s_ease]">
        {/* Header */}

        <div className="relative bg-gradient-to-r from-lime-600 to-green-600 px-8 py-7 text-white">
          <button onClick={onClose} className="absolute right-5 top-5 rounded-full bg-white/20 p-2 hover:bg-white/30">
            <X size={20} />
          </button>

          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 text-2xl font-bold">{getInitial(data.nama)}</div>

            <div>
              <h2 className="text-2xl font-bold">{data.nama}</h2>

              <span className="mt-2 inline-block rounded-full bg-white/20 px-3 py-1 text-sm">{data.status}</span>
            </div>
          </div>
        </div>

        {/* Body */}

        <div className="max-h-[70vh] overflow-y-auto p-8">
          {/* Info */}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
              <Phone size={18} className="text-lime-600" />

              <div>
                <p className="text-xs text-gray-500">Nomor Telepon</p>

                <p className="font-semibold">{data.no_hp}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4">
              <CalendarDays size={18} className="text-lime-600" />

              <div>
                <p className="text-xs text-gray-500">Tanggal</p>

                <p className="font-semibold">{formatTanggal(data.tanggal)}</p>
              </div>
            </div>
          </div>

          {/* Aspirasi */}

          <div className="mt-6 rounded-2xl bg-slate-50 p-5">
            <h4 className="mb-3 text-lg font-bold">Isi Aspirasi</h4>

            <p className="leading-8 whitespace-pre-wrap text-gray-700">{data.aspirasi}</p>
          </div>

          {/* Foto */}

          {data.foto && (
            <div className="mt-6">
              <h4 className="mb-4 text-lg font-bold">Foto Pendukung</h4>

              <img
                src={data.foto}
                alt="Foto Pendukung"
                className="
                  w-full
                  rounded-2xl
                  object-cover
                  max-h-[500px]
                  border
                "
              />
            </div>
          )}

          {/* Like & jumlah komentar */}

          <div className="mt-6 flex items-center gap-4 border-t pt-5">
            <button
              type="button"
              onClick={handleLike}
              disabled={liking}
              className={`flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition-colors ${data.liked_by_me ? "bg-lime-100 text-lime-700" : "bg-slate-50 text-gray-500 hover:bg-gray-100"}`}>
              <Heart size={18} className={data.liked_by_me ? "fill-lime-600 text-lime-600" : ""} />
              {data.total_like} Suka
            </button>

            <div className="flex items-center gap-2 rounded-full bg-slate-50 px-4 py-2 font-semibold text-gray-500">
              <MessageCircle size={18} />
              {data.komentar?.length ?? 0} Komentar
            </div>
          </div>

          {/* Form komentar */}

          <div className="mt-6 rounded-2xl bg-slate-50 p-5">
            <h4 className="mb-3 text-lg font-bold">Tulis Komentar</h4>

            <input value={nama} onChange={(e) => setNama(e.target.value)} placeholder="Nama kamu" className="mb-3 w-full rounded-xl border border-gray-200 bg-white px-4  py-2.5 outline-none focus:border-lime-400" />

            <div className="flex gap-2">
              <textarea
                value={komentarBaru}
                onChange={(e) => setKomentarBaru(e.target.value)}
                placeholder="Tulis komentar kamu..."
                rows={2}
                className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 outline-none focus:border-lime-400"
              />
            </div>
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={handleKirimKomentar}
                disabled={mengirim || !nama.trim() || !komentarBaru.trim()}
                className="flex shrink-0 items-center justify-center rounded-xl bg-lime-600  text-white transition hover:bg-lime-700 disabled:opacity-50 px-6 py-2.5">
                <Send size={18} />
              </button>
            </div>
          </div>

          {/* List komentar */}

          <div className="mt-6">
            <h4 className="mb-3 text-lg font-bold">Komentar ({data.komentar?.length ?? 0})</h4>

            {(!data.komentar || data.komentar.length === 0) && <p className="text-sm text-gray-400">Belum ada komentar, jadilah yang pertama.</p>}

            <div className="space-y-3">
              {data.komentar?.map((k) => (
                <div key={k.id} className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lime-100 text-xs font-bold text-lime-700">{getInitial(k.nama)}</div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-800">{k.nama}</p>
                      <span className="text-xs text-gray-400">{formatTanggalSingkat(k.tanggal)}</span>
                    </div>

                    <p className="mt-1 text-sm leading-6 text-gray-700">{k.komentar}</p>
                  </div>

                  {k.is_owner && (
                    <button
                      type="button"
                      onClick={() => handleHapusKomentar(k.id)}
                      disabled={hapusId === k.id}
                      className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                      aria-label="Hapus komentar">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}

        <div className="flex justify-end border-t bg-slate-50 px-8 py-5">
          <button
            onClick={onClose}
            className="
              rounded-xl
              bg-lime-600
              px-6
              py-3
              font-semibold
              text-white
              transition
              hover:bg-lime-700
            ">
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default AspirasiDetailModal;
